import { useState } from "react";
import { MessageSquarePlusIcon, XIcon } from "lucide-react";
import { useAuiState } from "@assistant-ui/react";
import { Thread } from "@/components/thread";
import { VehicleSidePanel } from "@/components/vehicle-side-panel";
import { Button } from "@/components/ui/button";
import { useT } from "@/locale-context";
import { cn } from "@/lib/utils";
import { useConversationControl } from "@/runtime/conversation-control";
import { useSidePanelStore } from "@/stores/side-panel-store";

export function ChatWidget() {
  const [open, setOpen] = useState(false);
  const t = useT();
  const panelOpen = useSidePanelStore((s) => s.content !== null);
  const { startNewConversation } = useConversationControl();
  const isRunning = useAuiState((s) => s.thread.isRunning);
  console.log("[ChatWidget] render", { panelOpen });

  const handleClose = () => {
    setOpen(false);
    useSidePanelStore.getState().close();
  };

  const handleStartNew = () => {
    if (isRunning) return;
    startNewConversation();
  };

  return (
    <>
      {/* Container — flex-row-reverse keeps the chat pinned to the right edge */}
      <div
        role="dialog"
        aria-label={t("widget.ariaLabel")}
        aria-hidden={!open}
        style={{ zIndex: 2147483000 }}
        className={cn(
          "fixed right-4 bottom-24 flex flex-row-reverse gap-2 transition-all duration-200 ease-out",
          "h-[calc(100dvh-8rem)] max-h-[640px]",
          panelOpen ? "w-[calc(100vw_-_2rem)] max-w-[56rem]" : "w-[calc(100vw_-_2rem)] max-w-md",
          open
            ? "translate-y-0 scale-100 opacity-100"
            : "pointer-events-none translate-y-2 scale-95 opacity-0",
        )}
      >
        {/* Chat column (first child → renders RIGHT due to flex-row-reverse) */}
        <div className="relative flex w-full max-w-md shrink-0 flex-col overflow-hidden rounded-2xl border border-blue-100 bg-white shadow-2xl md:w-md">
          {/* Header */}
          <div className="relative flex items-center justify-between gap-3 bg-white px-4 py-3 text-neutral-900">
            <div
              aria-hidden
              className="pointer-events-none absolute inset-y-0 right-0 w-1/2"
              style={{
                background:
                  "radial-gradient(ellipse at right top, rgba(88, 166, 255, 0.45) 0%, rgba(72, 255, 173, 0.25) 40%, transparent 75%)",
              }}
            />
            <div className="relative flex min-w-0 items-center">
              <div className="min-w-0">
                <div className="truncate text-base leading-tight font-bold tracking-wide text-blue-600">
                  {t("widget.title")}
                </div>
                <div className="truncate text-xs text-neutral-500">{t("widget.subtitle")}</div>
              </div>
            </div>
            <div className="relative flex items-center gap-1">
              <Button
                variant="ghost"
                size="icon-sm"
                onClick={handleStartNew}
                disabled={isRunning}
                aria-label={t("widget.startNew")}
                title={t("widget.startNew")}
                className="text-neutral-500 hover:bg-neutral-100 hover:text-neutral-700"
              >
                <MessageSquarePlusIcon />
              </Button>
              <Button
                variant="ghost"
                size="icon-sm"
                onClick={handleClose}
                aria-label={t("widget.close")}
                className="text-neutral-500 hover:bg-neutral-100 hover:text-neutral-700"
              >
                <XIcon />
              </Button>
            </div>
          </div>

          <div className="min-h-0 flex-1">
            <Thread />
          </div>
        </div>

        {/* Side panel (second child → renders LEFT due to flex-row-reverse) */}
        {panelOpen && <VehicleSidePanel className="flex min-w-0 flex-1" />}
      </div>

      {!open && (
        <button
          onClick={() => setOpen(true)}
          aria-expanded={open}
          aria-label={t("widget.openAriaLabel")}
          // Stable hook for the host page's "chat with us" call to action.
          // Matching on aria-label would break per locale, and matching on DOM
          // order is wrong: the dialog (and its close / start-new buttons)
          // renders before this launcher even while collapsed.
          data-chat-launcher=""
          style={{
            zIndex: 2147483000,
            background: "linear-gradient(87.09deg, #58A6FF -0.54%, #48FFAD 145.88%)",
            width: 350,
            height: 75,
            color: "white",
          }}
          className="fixed right-4 bottom-4 flex cursor-pointer items-center justify-between rounded-2xl border-0 px-7 shadow-lg transition-opacity hover:opacity-90"
        >
          <span className="text-lg font-normal">{t("widget.openButton")}</span>
          <img
            src="/assets/chat/agent.svg"
            alt={t("widget.agentAlt")}
            className="size-14 shrink-0"
          />
        </button>
      )}
    </>
  );
}
